'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Step {
  number: string;
  icon: string;
  title: string;
  text: string;
}

const steps: Step[] = [
  {
    number: '01',
    icon: 'AcademicCapIcon',
    title: 'Briefing',
    text: '15 minuti per capire equilibrio e controllo.',
  },
  {
    number: '02',
    icon: 'UserGroupIcon',
    title: 'In acqua',
    text: 'Parti in modo graduale con supporto costante.',
  },
  {
    number: '03',
    icon: 'SparklesIcon',
    title: 'Voli',
    text: 'Dopo pochi tentativi inizi a volare sull\'acqua',
  },
];

export default function FirstTimeSection({ lang = 'it' }: { lang?: 'it' | 'en' }) {
  if (lang !== 'it') return null;

  return (
    <section
      style={{
        background: 'linear-gradient(180deg, #0D2235 0%, #0F2840 100%)',
        borderTop: '1px solid rgba(0,212,255,0.08)',
        borderBottom: '1px solid rgba(0,212,255,0.08)',
      }}
    >
      <div
        className="container-main"
        style={{
          paddingTop: 'clamp(56px, 8vw, 96px)',
          paddingBottom: 'clamp(56px, 8vw, 96px)',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(48px, 7vw, 72px)' }}>
          <p
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: '#00D4FF',
              marginBottom: '14px',
            }}
          >
            PRIMA VOLTA? NESSUN PROBLEMA
          </p>
          <h2
            style={{
              fontSize: 'clamp(28px, 4vw, 44px)',
              fontWeight: 800,
              color: '#F0F7FF',
              lineHeight: 1.15,
              marginBottom: '16px',
              letterSpacing: '-0.02em',
            }}
          >
            È più semplice di quanto pensi
          </h2>
          <p
            style={{
              fontSize: 'clamp(15px, 1.8vw, 18px)',
              color: 'rgba(240,247,255,0.6)',
              maxWidth: '480px',
              margin: '0 auto',
              lineHeight: 1.6,
            }}
          >
            In pochi minuti impari le basi, entri in acqua con l&apos;istruttore e inizi a volare.
          </p>
        </div>

        {/* Horizontal steps with connecting line */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: '0',
            marginBottom: 'clamp(48px, 7vw, 64px)',
          }}
          className="steps-row"
        >
          {steps.map((step, idx) => (
            <div
              key={step.number}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                padding: '0 clamp(12px, 2vw, 32px)',
              }}
            >
              {/* Connector line — left half */}
              {idx > 0 && (
                <div
                  className="hidden sm:block"
                  style={{
                    position: 'absolute',
                    top: '28px',
                    left: '0',
                    width: '50%',
                    height: '1px',
                    background: 'linear-gradient(90deg, rgba(0,212,255,0.15) 0%, rgba(0,212,255,0.4) 100%)',
                  }}
                />
              )}
              {/* Connector line — right half */}
              {idx < steps.length - 1 && (
                <div
                  className="hidden sm:block"
                  style={{
                    position: 'absolute',
                    top: '28px',
                    right: '0',
                    width: '50%',
                    height: '1px',
                    background: 'linear-gradient(90deg, rgba(0,212,255,0.4) 0%, rgba(0,212,255,0.15) 100%)',
                  }}
                />
              )}

              {/* Icon circle */}
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'rgba(0,212,255,0.07)',
                  border: '1px solid rgba(0,212,255,0.22)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  position: 'relative',
                  zIndex: 1,
                  transition: 'background 0.25s ease, border-color 0.25s ease',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLDivElement;
                  el.style.background = 'rgba(0,212,255,0.15)';
                  el.style.borderColor = 'rgba(0,212,255,0.45)';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLDivElement;
                  el.style.background = 'rgba(0,212,255,0.07)';
                  el.style.borderColor = 'rgba(0,212,255,0.22)';
                }}
              >
                <Icon
                  name={step.icon as 'AcademicCapIcon'}
                  size={22}
                  style={{ color: '#00D4FF' }}
                />
              </div>

              {/* Title */}
              <h3
                style={{
                  fontSize: 'clamp(16px, 1.6vw, 20px)',
                  fontWeight: 700,
                  color: '#F0F7FF',
                  marginBottom: '10px',
                  letterSpacing: '-0.01em',
                  textAlign: 'center',
                }}
              >
                {step.title}
              </h3>

              {/* Text */}
              <p
                style={{
                  fontSize: 'clamp(13px, 1.2vw, 15px)',
                  color: 'rgba(240,247,255,0.5)',
                  lineHeight: 1.65,
                  margin: 0,
                  textAlign: 'center',
                  maxWidth: '200px',
                }}
              >
                {step.text}
              </p>
            </div>
          ))}
        </div>

        {/* Trust row + CTA */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <p
            style={{
              fontSize: '13px',
              color: 'rgba(240,247,255,0.4)',
              fontWeight: 500,
              letterSpacing: '0.02em',
              textAlign: 'center',
            }}
          >
            Nessuna esperienza richiesta&nbsp;&nbsp;•&nbsp;&nbsp;Attrezzatura inclusa&nbsp;&nbsp;•&nbsp;&nbsp;Istruttore sempre presente
          </p>

          <Link
            href="/experiences-pricing"
            className="btn-primary"
            style={{
              fontSize: '15px',
              padding: '16px 36px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            Scopri i pacchetti
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 640px) {
          .steps-row {
            flex-direction: column !important;
            align-items: center !important;
            gap: 40px !important;
          }
          .steps-row > div {
            width: 100%;
            max-width: 280px;
            padding: 0 !important;
          }
          .steps-row > div > div[style*="position: absolute"] {
            display: none !important;
          }
        }
      `}</style>
    </section>
  );
}
